import { useState } from "react";
import type { TrapPacketDto, TrapEventDto } from "@gryzonie/shared";
import { RawHexView } from "./RawHexView";

interface Props {
  packets: TrapPacketDto[];
  isLoading: boolean;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString("pl-PL", { hour12: false });
}

/** CR2032 lithium coin cell: nominal 3.0 V, usable down to ~2.0 V. */
function batteryColor(mv: number): string {
  if (mv >= 2900) return "text-success";
  if (mv >= 2500) return "text-warning";
  return "text-danger";
}

function batteryPercent(mv: number): number {
  // Rough linear mapping: 3.0 V → 100 %, 2.0 V → 0 %.
  const pct = ((mv - 2000) / (3000 - 2000)) * 100;
  return Math.max(0, Math.min(100, Math.round(pct)));
}

function eventColor(type: number): string {
  switch (type) {
    case 0x01:
      return "ctp-yellow"; // Lid opened
    case 0x02:
      return "ctp-green"; // Lid closed
    case 0x03:
    case 0x04:
      return "ctp-red"; // Mouse caught
    case 0x05:
      return "ctp-peach"; // Low battery
    default:
      return "ctp-overlay0";
  }
}

function EventBadge({ event }: { event: TrapEventDto }) {
  const color = eventColor(event.type);
  return (
    <span
      className="inline-flex items-center gap-2 rounded px-2 py-0.5 text-xs"
      style={{
        backgroundColor: `color-mix(in srgb, var(--${color}) 15%, transparent)`,
        border: `1px solid color-mix(in srgb, var(--${color}) 50%, transparent)`,
        color: `var(--${color})`,
      }}
    >
      <span className="font-mono text-[10px] opacity-80">
        0x{event.type.toString(16).padStart(2, "0")}
      </span>
      <span>{event.typeLabel}</span>
      <span className="font-mono text-[10px] text-subtext0">
        {formatTime(event.at)}
      </span>
      <span className="text-overlay0">(+{event.offsetSec}s)</span>
    </span>
  );
}

export function TrapTable({ packets, isLoading }: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="rounded border border-surface0 bg-mantle p-6 text-sm text-subtext0">
        Wczytywanie pakietów…
      </div>
    );
  }

  if (packets.length === 0) {
    return (
      <div className="rounded border border-surface0 bg-mantle p-6 text-sm text-subtext0">
        Brak pakietów. Czekam na dane z pułapek…
      </div>
    );
  }

  return (
    <div className="overflow-auto rounded border border-surface0 bg-mantle">
      <table className="min-w-full text-sm">
        <thead className="bg-crust text-left text-xs uppercase tracking-wider text-subtext0">
          <tr>
            <th className="px-3 py-2">Odebrano</th>
            <th className="px-3 py-2">Device ID</th>
            <th className="px-3 py-2">PID</th>
            <th className="px-3 py-2">Temp.</th>
            <th className="px-3 py-2">Wilg.</th>
            <th className="px-3 py-2">Bateria (CR2032)</th>
            <th className="px-3 py-2">Zdarzenia</th>
            <th className="px-3 py-2">RAW</th>
          </tr>
        </thead>
        <tbody>
          {packets.map((p, i) => {
            const key = p.id !== undefined ? `id-${p.id}` : `idx-${i}-${p.receivedAt}`;
            const open = expanded.has(key);
            return (
              <Row
                key={key}
                packet={p}
                rowKey={key}
                open={open}
                onToggle={() => toggle(key)}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Row({
  packet,
  open,
  onToggle,
}: {
  packet: TrapPacketDto;
  rowKey: string;
  open: boolean;
  onToggle: () => void;
}) {
  const pct = batteryPercent(packet.batteryVoltageMv);
  return (
    <>
      <tr className="border-t border-surface0 align-top hover:bg-surface0/40">
        <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
          {formatTime(packet.receivedAt)}
        </td>
        <td className="px-3 py-2 font-mono text-xs">{packet.deviceId}</td>
        <td className="px-3 py-2">{packet.pid}</td>
        <td className="px-3 py-2 whitespace-nowrap">
          {packet.temperatureC.toFixed(2)}°C
        </td>
        <td className="px-3 py-2">{packet.humidity}%</td>
        <td className="px-3 py-2 whitespace-nowrap">
          <div className={`flex flex-col gap-0.5 ${batteryColor(packet.batteryVoltageMv)}`}>
            <span className="font-mono">{packet.batteryVoltageV.toFixed(3)} V</span>
            <span className="text-[10px] text-subtext0">{pct}%</span>
          </div>
        </td>
        <td className="px-3 py-2">
          {packet.events.length === 0 ? (
            <span className="text-overlay0">—</span>
          ) : (
            <div className="flex flex-col gap-1">
              {packet.events.map((e) => (
                <EventBadge key={e.index} event={e} />
              ))}
            </div>
          )}
        </td>
        <td className="px-3 py-2">
          <button
            type="button"
            onClick={onToggle}
            className="rounded border border-surface1 bg-surface0 px-2 py-0.5 text-xs hover:bg-surface1"
          >
            {open ? "Ukryj" : "Pokaż"}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="border-t border-surface0 bg-base/40">
          <td colSpan={8} className="px-3 py-3">
            <div className="space-y-3 text-xs">
              <div>
                <span className="text-subtext0">Device timestamp:</span>{" "}
                <span className="font-mono">{formatTime(packet.deviceTimestamp)}</span>
              </div>
              <div>
                <div className="mb-1 text-subtext0">RAW (hex):</div>
                <RawHexView rawHex={packet.rawHex} eventCount={packet.eventCount} />
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
